/**
 * XP / level math.
 *
 * Pure functions — no DB, no React. The award engine bumps XP, the
 * UI calls `levelFromXp` and `progressInLevel` to render the bar.
 *
 * Curve choice:
 *   xpForLevel(n) = 50 * (n - 1) * (n - 1)
 *
 *   level  threshold      gap to next
 *   1      0              50
 *   2      50             150
 *   3      200            250
 *   5      800            450
 *   10     4050           950
 *   25     28800          2450
 *   50     120050         4950
 *
 * Quadratic ramp keeps low levels reachable in the first session (so
 * new users see the XP bar move) while making higher levels meaningful
 * milestones rather than rapid grinds.
 *
 * Capped at LEVEL_MAX = 100 to keep `level` a smallint-friendly value
 * and avoid pathological curve growth.
 */

export const LEVEL_MAX = 100;

/** XP needed to reach a given level (level 1 = 0 XP). */
export function xpForLevel(level: number): number {
  const clamped = Math.max(1, Math.min(LEVEL_MAX, Math.floor(level)));
  return 50 * (clamped - 1) * (clamped - 1);
}

/** Inverse: highest level reachable at the given total XP. */
export function levelFromXp(xp: number): number {
  if (!Number.isFinite(xp) || xp <= 0) return 1;
  // n - 1 = sqrt(xp / 50) → n = floor(sqrt(xp/50)) + 1
  const candidate = Math.floor(Math.sqrt(xp / 50)) + 1;
  return Math.max(1, Math.min(LEVEL_MAX, candidate));
}

export interface LevelProgress {
  level: number;
  /** Total XP. */
  xp: number;
  /** XP threshold to reach the current level. */
  currentThreshold: number;
  /** XP threshold to reach the next level (Infinity if at LEVEL_MAX). */
  nextThreshold: number;
  /** XP earned inside the current level. */
  intoLevel: number;
  /** XP needed for the current level span. */
  spanXp: number;
  /** Fractional progress through current level [0, 1]. 1 if at LEVEL_MAX. */
  progress: number;
}

/** All level-bar fields a UI needs in one call. */
export function progressInLevel(xp: number): LevelProgress {
  const level = levelFromXp(xp);
  const currentThreshold = xpForLevel(level);
  if (level >= LEVEL_MAX) {
    return {
      level,
      xp,
      currentThreshold,
      nextThreshold: Number.POSITIVE_INFINITY,
      intoLevel: xp - currentThreshold,
      spanXp: Number.POSITIVE_INFINITY,
      progress: 1,
    };
  }
  const nextThreshold = xpForLevel(level + 1);
  const intoLevel = xp - currentThreshold;
  const spanXp = nextThreshold - currentThreshold;
  return {
    level,
    xp,
    currentThreshold,
    nextThreshold,
    intoLevel,
    spanXp,
    progress: spanXp > 0 ? intoLevel / spanXp : 1,
  };
}

/**
 * XP awarded per primitive action. Centralised so the award engine,
 * UI tooltips, and tests all agree.
 *
 * Tuning rationale:
 *   - Reviews are the most-common action. Keep small.
 *   - Block exam questions are correctly-answered points (not 'tried');
 *     ~20 questions correct ≈ one streak-day's worth of XP, which
 *     feels right for a 15-min exam.
 *   - ATSA modules are bigger commitments (10–20 min each); reward
 *     accordingly.
 *   - Phraseology attempts include audio recording; a higher per-unit
 *     reward keeps the trainer feeling worth-using even for short
 *     sessions.
 */
export const XP_AWARD = {
  reviewAgain: 0,
  reviewHard: 5,
  reviewGood: 10,
  reviewEasy: 15,
  blockExamPerCorrect: 10,
  atsaModuleSubmit: 50,
  phraseologyAttempt: 10,
  streakDay: 25,
} as const;
